import { Zap, Repeat } from 'lucide-react';
import StaffPicker from '../StaffPicker';
import { useContextBatches } from '../../hooks/useContextBatches';

const TASK_TYPES = ['STANDARD', 'CALL', 'READING', 'PAYMENT'];
const ENERGY_LEVELS = ['HIGH', 'MEDIUM', 'LOW'];
const RECURRENCE_RULES = ['NONE', 'DAILY', 'WEEKLY', 'MONTHLY'];

/**
 * Classification block of the task form: type, energy, recurrence,
 * context batch, and delegation.
 */
export function TaskMetaSection({ form, setForm }) {
  // User-customisable batches (Settings → Context batches).
  const { batches } = useContextBatches();
  // Keep a task's existing tag visible/selectable even if the user
  // deleted that batch from Settings after tagging.
  const orphanContext = form.context && !batches.some((b) => b.key === form.context)
    ? { key: form.context, label: form.context.replace(/_/g, ' ') }
    : null;
  return (
    <>
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="text-[10px] uppercase tracking-[0.08em] font-semibold block mb-1.5" style={{ color: 'var(--pd-subtle)' }}>Type</label>
          <select value={form.taskType} onChange={(e) => setForm((prev) => ({ ...prev, taskType: e.target.value }))} className="w-full px-3 py-3 rounded-[16px] text-sm" style={{ border: '1px solid var(--pd-border)', background: 'var(--pd-panel-solid)' }}>
            {TASK_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.08em] font-semibold block mb-1.5" style={{ color: 'var(--pd-subtle)' }}><Zap className="w-3 h-3 inline mr-1" />Energy</label>
          <div className="flex gap-1.5">
            {ENERGY_LEVELS.map((energy) => (
              <button key={energy} type="button" onClick={() => setForm((prev) => ({ ...prev, energy }))} className="flex-1 py-2.5 rounded-xl text-[11px] font-medium transition-colors" style={{ background: form.energy === energy ? 'var(--pd-panel)' : 'transparent', color: form.energy === energy ? 'var(--pd-text)' : 'var(--pd-subtle)', border: form.energy === energy ? '1px solid var(--pd-border)' : '1px solid transparent' }}>{energy}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="text-[10px] uppercase tracking-[0.08em] font-semibold block mb-1.5" style={{ color: 'var(--pd-subtle)' }}><Repeat className="w-3 h-3 inline mr-1" />Recurrence</label>
          <div className="flex gap-1.5">
            {RECURRENCE_RULES.map((rule) => (
              <button key={rule} type="button" data-testid={`task-recurrence-${rule.toLowerCase()}`} onClick={() => setForm((prev) => ({ ...prev, recurrenceRule: rule }))} className="flex-1 py-2.5 rounded-xl text-[10px] font-semibold transition-colors" style={{ background: form.recurrenceRule === rule ? 'var(--pd-focus)' : 'var(--pd-panel-2)', color: form.recurrenceRule === rule ? 'white' : 'var(--pd-subtle)' }}>{rule === 'NONE' ? 'Once' : rule.charAt(0) + rule.slice(1).toLowerCase()}</button>
            ))}
          </div>
          {form.recurrenceRule !== 'NONE' && (
            <input data-testid="task-recurrence-end-date" type="date" value={form.recurrenceEndDate} onChange={(e) => setForm(prev => ({ ...prev, recurrenceEndDate: e.target.value }))} placeholder="End date (optional)" className="w-full mt-2 px-3 py-2.5 rounded-[16px] text-sm" style={{ border: '1px solid var(--pd-border)', background: 'var(--pd-panel-solid)' }} />
          )}
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.08em] font-semibold block mb-1.5" style={{ color: 'var(--pd-subtle)' }}>Context batch</label>
          <div className="flex flex-wrap gap-1">
            <button type="button" data-testid="task-context-none" onClick={() => setForm(prev => ({ ...prev, context: '' }))} className="px-2 py-1.5 rounded-lg text-[10px] font-semibold" style={{ background: !form.context ? 'var(--pd-text)' : 'var(--pd-panel-2)', color: !form.context ? 'var(--bg-canvas)' : 'var(--pd-subtle)' }}>None</button>
            {[...batches, ...(orphanContext ? [orphanContext] : [])].map(ctx => (
              <button key={ctx.key} type="button" data-testid={`task-context-${ctx.key.toLowerCase()}`} onClick={() => setForm(prev => ({ ...prev, context: ctx.key }))} className="px-2 py-1.5 rounded-lg text-[10px] font-semibold transition-colors" style={{ background: form.context === ctx.key ? 'var(--pd-focus)' : 'var(--pd-panel-2)', color: form.context === ctx.key ? 'white' : 'var(--pd-subtle)' }}>
                {ctx.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <StaffPicker
            value={form.delegatedTo}
            staffId={form.staffId}
            businessId={form.businessId}
            onChange={({ staffId, delegatedTo }) => setForm((prev) => ({ ...prev, staffId, delegatedTo }))}
          />
          {form.delegatedTo && (
            <input data-testid="task-delegation-notes" value={form.delegationNotes} onChange={(e) => setForm(prev => ({ ...prev, delegationNotes: e.target.value }))} placeholder="Delegation notes..." className="w-full mt-2 px-3 py-2.5 rounded-[16px] text-sm" style={{ border: '1px solid var(--pd-border)', background: 'var(--pd-panel-solid)', color: 'var(--pd-text)' }} />
          )}
        </div>
      </div>
    </>
  );
}
